// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Audit event types and interfaces for admin UI.
 * Maps to backend AuditEventDTO and related types.
 */

/**
 * Actor information (who performed the action).
 */
export interface AuditActor {
  userId: string;
  username: string;
  email?: string;
  role?: string;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Tenant information (which brokerage).
 */
export interface AuditTenant {
  subdomain: string;
  brokerageId: string;
}

/**
 * Resource information (what was affected).
 */
export interface AuditResource {
  type: string;
  id: string;
  name?: string;
}

/**
 * Metadata about the event.
 */
export interface AuditMetadata {
  correlationId?: string;
  sessionId?: string;
  transactionId?: string;
  processedAt?: string;
  signature?: string;
  signatureValid?: boolean;
}

/**
 * Main audit event interface.
 */
export interface AuditEvent {
  eventId: string;
  timestamp: string;
  category: string;
  action: string;
  severity: string;
  description?: string;
  actor: AuditActor;
  tenant: AuditTenant;
  resource: AuditResource;
  changes?: Record<string, unknown>;
  metadata: AuditMetadata;
}

/**
 * Filter parameters for audit event queries.
 */
export interface AuditQueryFilters {
  brokerageId?: string;
  category?: string;
  action?: string;
  severity?: string;
  userId?: string;
  resourceType?: string;
  resourceId?: string;
  startDate?: string;
  endDate?: string;
  searchText?: string;
}

/**
 * User activity aggregation response.
 */
export interface UserActivityResponse {
  userId: string;
  brokerageId: string;
  totalEvents: number;
  eventsByCategory: Record<string, number>;
  eventsByAction: Record<string, number>;
  eventsBySeverity: Record<string, number>;
  activityByHour: Record<number, number>;
  activityByDayOfWeek: Record<number, number>;
  ipAddresses: string[];
  recentEvents: AuditEvent[];
}

/**
 * Pagination parameters.
 */
export interface PaginationParams {
  page: number;
  size: number;
  sort?: string;
}

/**
 * Paginated response wrapper.
 */
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

/**
 * Export format options.
 */
export type ExportFormat = 'CSV' | 'JSON';

/**
 * Audit categories (from backend).
 */
export enum AuditCategory {
  BROKERAGE = 'BROKERAGE',
  ADVISOR = 'ADVISOR',
  PROFILE = 'PROFILE',
  CLIENT = 'CLIENT',
  POLICY = 'POLICY',
  LOA = 'LOA',
  EMAIL = 'EMAIL',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  SYSTEM = 'SYSTEM'
}

/**
 * Common audit actions.
 */
export enum AuditAction {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  SIGN = 'SIGN',
  SEND = 'SEND',
  VOID = 'VOID',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  ACCESS_DENIED = 'ACCESS_DENIED'
}

/**
 * Severity levels.
 */
export enum AuditSeverity {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  CRITICAL = 'CRITICAL'
}
